import React, { useRef, useCallback } from 'react';
import useUniversalUploadWidget from 'Hooks/useUniversalUploadWidget';

export interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: string;
    file?: File;
    thumbnailUrl?: string;
    iconType: 'pdf' | 'csv' | 'image' | 'video' | 'audio' | 'document' | 'excel' | 'word';
    uploadProgress: number;
    status: 'pending' | 'uploading' | 'processing' | 'ready' | 'error';
    uploadedAssetId?: number;
    s3Url?: string; // S3 URL for the uploaded file
    errorMessage?: string;
    retryCount?: number; // Track number of retry attempts
}

interface UniversalUploadWidgetProps {
    maxFiles?: number;
    maxFileSize?: number; // in bytes
    acceptedTypes?: string[];
    allowMultiple?: boolean;
    chatId?: string;
    className?: string;
    externalFiles?: File[]; // For drag & drop integration
}

const UniversalUploadWidget: React.FC<UniversalUploadWidgetProps> = ({
    maxFiles = 10,
    maxFileSize = 100 * 1024 * 1024, // 100MB default
    allowMultiple = true,
    chatId,
    className = '',
    externalFiles
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // Use the upload widget store
    const {
        supportedFileTypes,
        handleUploadStart,
        uploadFiles,
        setFileInputRef
    } = useUniversalUploadWidget();

    // Register file input ref with the store
    React.useEffect(() => {
        setFileInputRef(fileInputRef.current);
        
        // Cleanup when component unmounts
        return () => {
            setFileInputRef(null);
        };
    }, [fileInputRef]);

    const getFileIconType = (type: string): UploadedFile['iconType'] => {
        if (type.startsWith('image/')) return 'image';
        if (type.startsWith('video/')) return 'video';
        if (type.startsWith('audio/')) return 'audio';
        if (type === 'application/pdf') return 'pdf';
        if (type === 'text/csv' || type === 'application/csv') return 'csv';
        if (type.includes('excel') || type.includes('spreadsheet')) return 'excel';
        if (type.includes('word') || type.includes('document')) return 'word';
        return 'document';
    };

    const generateThumbnail = (file: File): Promise<string | undefined> => {
        return new Promise((resolve) => {
            if (!file.type.startsWith('image/')) {
                resolve(undefined);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Thumbnail size
                    const maxSize = 200;
                    let { width, height } = img;

                    if (width > height) {
                        if (width > maxSize) {
                            height = (height * maxSize) / width;
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width = (width * maxSize) / height;
                            height = maxSize;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    ctx?.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                };
                img.src = e.target?.result as string;
            };
            reader.readAsDataURL(file);
        });
    };

    const validateFile = (file: File): string | null => {
        // Check if file type is accepted
        const isAccepted = supportedFileTypes.some(acceptedType => {
            // Handle wildcards like 'image/*' or 'video/*'
            if (acceptedType.endsWith('/*')) {
                const prefix = acceptedType.slice(0, -2);
                return file.type.startsWith(prefix + '/');
            }
            // Handle file extensions like '.jpg'
            if (acceptedType.startsWith('.')) {
                return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
            }
            // Handle exact MIME types
            return file.type === acceptedType;
        });

        if (!isAccepted) {
            return `File type "${file.type}" is not supported`;
        }
        if (file.size > maxFileSize) {
            return `File size exceeds ${Math.round(maxFileSize / 1024 / 1024)}MB limit`;
        }
        return null;
    };

    const processFiles = async (fileList: FileList) => {
        const files = Array.from(fileList);

        if (files.length > maxFiles) {
            alert(`You can only upload ${maxFiles} files at once`);
            return;
        }

        const validFiles: UploadedFile[] = [];

        for (const file of files) {
            const error = validateFile(file);
            if (error) {
                alert(`${file.name}: ${error}`);
                continue;
            }

            const thumbnailUrl = await generateThumbnail(file);

            const uploadedFile: UploadedFile = {
                id: `${Date.now()}-${Math.random()}`,
                name: file.name,
                size: file.size,
                type: file.type,
                file,
                thumbnailUrl,
                iconType: getFileIconType(file.type),
                uploadProgress: 0,
                status: 'pending'
            };

            validFiles.push(uploadedFile);
        }

        if (validFiles.length === 0) return;

        // Use store method to start upload
        handleUploadStart(validFiles);

        // Start uploading files using store
        await uploadFiles(validFiles, chatId);
    };

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            processFiles(files);
        }
        // Reset input value to allow re-uploading same file
        e.target.value = '';
    }, []);

    // Process external files when prop changes (for drag & drop integration)
    React.useEffect(() => {
        if (externalFiles && externalFiles.length > 0) {
            // Convert File[] to FileList-like object
            const fileList = {
                length: externalFiles.length,
                item: (index: number) => externalFiles[index],
                [Symbol.iterator]: function* () {
                    for (let i = 0; i < this.length; i++) {
                        yield this.item(i);
                    }
                }
            } as FileList;

            processFiles(fileList);
        }
    }, [externalFiles]);

    return (
        <input
            ref={fileInputRef}
            type="file"
            multiple={allowMultiple}
            accept={supportedFileTypes.join(',')}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
        />
    );
};

export default UniversalUploadWidget; 